// 🗺️ 전국 지역썰 카탈로그 — 지역 카드 메타·방언 프로필·샘플 소재 SSOT.
//   대본은 선택 지역의 현대 생활 사투리가 기본이다. 도시 고유형 근거가 약하면 광역권 말끝·호흡을 쓰고 희귀어를 만들지 않는다.
import {
  REGIONAL_VOICE_PROFILES,
  type RegionalVoiceProfileId,
} from "../../../supabase/functions/_shared/regional-voice-profiles";

export type RegionalStyleId =
  | "ssulseoul" | "ssulincheon" | "ssulsuwon"
  | "ssulchuncheon" | "ssulgangneung"
  | "ssuldaejeon" | "ssulcheongju" | "ssulcheonan"
  | "ssuljeonju" | "ssulgunsan"
  | "ssulgwangju" | "ssulmokpo" | "ssulyeosu" | "ssulsuncheon"
  | "ssulbusan" | "ssululsan" | "ssulchangwon" | "ssuljinju" | "ssultongyeong"
  | "ssuldaegu" | "ssulyeongcheon" | "ssulgyeongju" | "ssulandong" | "ssulpohang"
  | "ssuljeju";

export interface RegionalStyleSpec {
  id: RegionalStyleId;
  label: string;
  emoji: string;
  voiceProfileId: RegionalVoiceProfileId;
  /** 지역 카드가 다루는 생활 소재. 실재 시설의 현재 상태·수치 주장이 아닌 범용 소재만 둔다. */
  topic: string;
  sampleTitle: string;
  sampleQuestion: string;
  sampleClosing: string;
  sampleAssetPrefix: string;
  sampleAssetCount: number;
  /** 0-based 씬. 시작 +19f를 썸네일로 쓴다. */
  thumbSceneIndex: number;
  /** 도시/광역권 검증 규칙. 지역 나레이션 기본값과 과장 금지를 함께 소유한다. */
  dialectRule?: string;
}

const spec = (
  id: RegionalStyleId,
  label: string,
  emoji: string,
  voiceProfileId: RegionalVoiceProfileId,
  topic: string,
  sampleTitle: string,
  sampleQuestion: string,
  sampleClosing: string,
  sampleAssetPrefix: string,
  sampleAssetCount: number,
  thumbSceneIndex: number,
  dialectRule?: string,
): RegionalStyleSpec => ({
  id, label, emoji, voiceProfileId, topic, sampleTitle, sampleQuestion, sampleClosing,
  sampleAssetPrefix, sampleAssetCount, thumbSceneIndex, dialectRule,
});

const CAPITAL_RULE = "수도권 현대 구어를 쓴다. 별도 사투리 어휘를 만들지 말고 표준어 기반 전문체로 자연스럽게 읽는다.";

const REGIONAL_NARRATION_DEFAULT = "새로 쓰거나 고치는 모든 나레이션을 선택 지역의 현대 생활 사투리로 쓴다. 질문·인용·마지막 한 줄에만 사투리를 묻히지 말고 도입부터 결말까지 지역 말끝·호흡이 자연스럽게 이어지게 한다. 단 모든 문장을 한 어미로 반복하거나 희귀어·억지 음차를 만들지 마라.";
const regionalDefault = (rule: string) => `${REGIONAL_NARRATION_DEFAULT} ${rule}`;

const GANGWON_DEFAULT = regionalDefault("강원권 현대 생활말의 말끝과 호흡을 유지한다. 문헌 근거 없는 '-드래요'를 반복하거나 산골 말투를 흉내 내지 마라.");
const CHUNGCHEONG_DEFAULT = regionalDefault("충청권 존대형 '-유'를 해요체가 자연스러운 서술·인용·질문에 고르게 쓴다. '-겨'는 '거야'에 대응하는 맥락이 분명할 때만 쓰고, 느린 사람을 흉내 내는 과장은 금지한다.");
const JEOLLA_SAFE_RULE = regionalDefault("전라권 현대 생활말의 말끝·어순·호흡을 나레이션 전반에 유지한다. '-랑께'·'-당께'·'-부러'는 선택 도시의 용법과 문맥이 자연스러울 때만 쓰고 자동 삽입하지 마라.");
const GWANGJU_RULE = regionalDefault("광주·전남 서부 현대 생활말을 나레이션 전반에 유지한다. '-게요'·'-시게요'는 제안·청유 또는 정중하고 완곡한 명령에만 쓰고 평서문의 보편적 말끝으로 붙이지 마라.");
const GYEONGSANG_QUESTION_RULE = regionalDefault("경상권 현대 생활말의 평서·서술 말끝과 호흡도 계속 유지한다. 의문사가 없는 판정 질문은 용언 뒤 '-나', '이다/아니다' 뒤 '-가', 의문사가 있는 설명 질문은 용언 뒤 '-노', '이다/아니다' 뒤 '-고'를 쓴다. '-노'를 평서·감탄 종결로 쓰지 마라.");
const YEONGCHEON_RULE = regionalDefault("영천·경북 중동부 현대 생활말을 나레이션 전반에 유지한다. 정중한 평서에 '-ㅂ니더'·'-십더', 정중한 질문에 '-능교'를 쓸 수 있다. '-니껴'를 영천 전역의 대표형으로 단정하지 마라.");
const ANDONG_RULE = regionalDefault("경북 북부 현대 생활말을 나레이션 전반에 유지한다. 정중한 질문의 '-니껴'와 설명의 '-시더'를 문맥에 맞게 쓰되, 모든 문장을 같은 어미로 끝내지 마라.");
const JEJU_RULE = regionalDefault("현대 제주 화자의 일상적 말끝과 호흡을 나레이션 전반에 유지한다. 검증되지 않은 옛 제주 어휘·아래아 표기를 만들거나 전국 독자가 이해할 수 없게 과장하지 마라.");

export const REGIONAL_STYLE_SPECS: RegionalStyleSpec[] = [
  spec("ssulseoul", "서울썰", "🏙️", "capital-standard", "오래된 골목과 새 동네가 겹치는 일상", "밤보다 늦게 잠드는 골목", "이 골목은 왜 밤보다 늦게 자나?", "밤보다 늦게 잠드는 골목이라는 말이 돈다고", "dongnessul", 4, 5, CAPITAL_RULE),
  spec("ssulincheon", "인천썰", "⚓", "capital-standard", "항구와 섬을 오가는 동네 소문", "바람이 먼저 도착하는 항구", "누가 바람보다 먼저 소식을 가져오나?", "바람이 먼저 도착하는 항구라네요", "naturessul", 4, 4, CAPITAL_RULE),
  spec("ssulsuwon", "수원썰", "🏰", "capital-standard", "성곽 곁 골목과 장터의 일상", "담장보다 오래 남은 인사", "이 인사는 언제부터 남았을까?", "담장보다 오래 남은 인사라는 말이 돈다고", "dongnessul", 4, 6, CAPITAL_RULE),
  spec("ssulchuncheon", "춘천썰", "🏞️", "gangwon-inland", "호수와 시장 사이 동네 이야기", "안개보다 먼저 여는 가게", "누가 안개보다 먼저 문을 열었을까?", "안개보다 먼저 여는 가게라네요", "localdocu", 5, 3, GANGWON_DEFAULT),
  spec("ssulgangneung", "강릉썰", "🌅", "gangwon-east", "바다와 산이 맞닿은 동네 소문", "해보다 먼저 깨어난 바닷길", "이 길은 왜 해보다 먼저 깨나?", "해보다 먼저 깨어난 바닷길이라는 말이 돈다고", "naturessul", 4, 4, GANGWON_DEFAULT),
  spec("ssuldaejeon", "대전썰", "🚉", "chungcheong", "역과 시장을 오가는 느긋한 일상", "기차보다 먼저 도착한 안부", "이 안부가 먼저 온 겨?", "기차보다 먼저 도착한 안부였대유", "localdocu", 5, 5, CHUNGCHEONG_DEFAULT),
  spec("ssulcheongju", "청주썰", "🌳", "chungcheong", "도심과 오래된 골목의 생활 소문", "골목이 먼저 기억한 이름", "그 이름 아직 남은 겨?", "골목이 먼저 기억한 이름이래유", "dongnessul", 4, 6, CHUNGCHEONG_DEFAULT),
  spec("ssulcheonan", "천안썰", "🍐", "chungcheong", "길목과 장터에서 오가는 이야기", "길보다 먼저 생긴 약속", "이 약속이 먼저였던 겨?", "길보다 먼저 생긴 약속이래유", "localdocu", 5, 4, CHUNGCHEONG_DEFAULT),
  spec("ssuljeonju", "전주썰", "🏘️", "jeonbuk", "한옥 골목과 장터의 생활 기억", "담장 너머 먼저 온 냄새", "무슨 냄새가 먼저 골목을 돌았을까?", "담장 너머 먼저 온 냄새라는 말이 돈다고", "gohyangssul", 4, 5, JEOLLA_SAFE_RULE),
  spec("ssulgunsan", "군산썰", "🌾", "jeonbuk", "항구와 오래된 거리의 일상", "시간보다 천천히 닫힌 문", "그 문은 왜 늦게 닫혔을까?", "시간보다 천천히 닫힌 문이라네요", "gohyangssul", 4, 6, JEOLLA_SAFE_RULE),
  spec("ssulgwangju", "광주썰", "🌸", "jeonnam-west", "동네 골목의 인사와 생활 제보", "꽃보다 먼저 피는 동네 인사", "이 얘기 같이 들어보시게요", "사람 사이 인사라는 말이 돈한다고", "dongnessul", 4, 5, GWANGJU_RULE),
  spec("ssulmokpo", "목포썰", "⛴️", "jeonnam-west", "항구와 언덕 골목의 소문", "배보다 먼저 돌아온 소식", "그 소식은 어디서 먼저 왔을까?", "배보다 먼저 돌아온 소식이라네요", "naturessul", 4, 4, JEOLLA_SAFE_RULE),
  spec("ssulyeosu", "여수썰", "🌊", "jeonnam-east", "섬과 바닷길 사이 생활 이야기", "등대보다 먼저 켜진 창", "누가 등대보다 먼저 불을 켰을까?", "등대보다 먼저 켜진 창이라는 말이 돈한다고", "naturessul", 4, 5, JEOLLA_SAFE_RULE),
  spec("ssulsuncheon", "순천썰", "🦆", "jeonnam-east", "들판과 시장을 잇는 동네 소문", "바람보다 오래 남은 발자국", "그 발자국은 어디로 이어졌을까?", "바람보다 오래 남은 발자국이라네요", "localdocu", 5, 6, JEOLLA_SAFE_RULE),
  spec("ssulbusan", "부산썰", "🌊", "gyeongnam-east", "바다·골목·시장의 생활 제보", "해보다 먼저 깨어나는 바닷길", "대체 누가 먼저 깨우노?", "바다가 아니라 그 길이라는 말이 돈한다고", "naturessul", 4, 4, GYEONGSANG_QUESTION_RULE),
  spec("ssululsan", "울산썰", "🌉", "gyeongnam-east", "강과 바다가 만나는 동네 이야기", "다리보다 먼저 건넌 소문", "그 소문은 어디서 건너왔노?", "다리보다 먼저 건넌 소문이라네요", "naturessul", 4, 5, GYEONGSANG_QUESTION_RULE),
  spec("ssulchangwon", "창원썰", "🌳", "gyeongnam-east", "계획도시와 오래된 장터의 일상", "길보다 먼저 자리 잡은 나무", "이 나무가 먼저 있었나?", "길보다 먼저 자리 잡은 나무라는 말이 돈한다고", "dongnessul", 4, 6, GYEONGSANG_QUESTION_RULE),
  spec("ssuljinju", "진주썰", "🏯", "gyeongnam-west", "강변과 장터에서 전해지는 소문", "물보다 오래 흐른 이야기", "이 이야기는 어디서 시작됐노?", "물보다 오래 흐른 이야기라네요", "localdocu", 5, 4, GYEONGSANG_QUESTION_RULE),
  spec("ssultongyeong", "통영썰", "🐚", "gyeongnam-west", "섬과 항구 골목의 생활 이야기", "배보다 먼저 길을 안 골목", "그 골목은 어디로 이어지노?", "배보다 먼저 길을 안 골목이라는 말이 돈한다고", "naturessul", 4, 5, GYEONGSANG_QUESTION_RULE),
  spec("ssuldaegu", "대구썰", "☀️", "gyeongbuk-east", "시장과 골목의 빠른 생활 소문", "해보다 먼저 달아오른 장터", "누가 먼저 장터를 깨웠노?", "해보다 먼저 달아오른 장터라네요", "localdocu", 5, 5, GYEONGSANG_QUESTION_RULE),
  spec("ssulyeongcheon", "영천썰", "🌾", "gyeongbuk-east", "장터·농촌·골목의 일상 소문", "해보다 먼저 여는 장터 문", "어디서 시작됐능교?", "해보다 먼저 여는 문이 됐다고 합니더", "localdocu", 5, 3, YEONGCHEON_RULE),
  spec("ssulgyeongju", "경주썰", "🏛️", "gyeongbuk-east", "옛길과 새 골목이 겹치는 이야기", "돌보다 오래 남은 약속", "그 약속은 언제 생겼노?", "돌보다 오래 남은 약속이라네요", "ssullocal", 10, 5, GYEONGSANG_QUESTION_RULE),
  spec("ssulandong", "안동썰", "🎭", "gyeongbuk-north", "마을과 장터의 오래된 구전", "문보다 먼저 열린 인사", "이 인사 아직 남았니껴?", "문보다 먼저 열린 인사였시더", "ssulpungsok", 10, 6, ANDONG_RULE),
  spec("ssulpohang", "포항썰", "🌅", "gyeongbuk-east", "바다와 시장 사이 생활 소문", "파도보다 먼저 번진 소식", "그 소식은 어디서 왔노?", "파도보다 먼저 번진 소식이라네요", "naturessul", 4, 4, GYEONGSANG_QUESTION_RULE),
  spec("ssuljeju", "제주썰", "🍊", "jeju", "바람·돌담·바닷길의 생활 기억", "바람보다 오래 서 있던 돌담", "그 돌담은 무엇을 기억할까?", "바람보다 오래 서 있던 돌담이라는 말이 돈한다고", "naturessul", 4, 6, JEJU_RULE),
];

export const REGIONAL_STYLE_IDS = REGIONAL_STYLE_SPECS.map((s) => s.id);
const SPEC_BY_ID = new Map(REGIONAL_STYLE_SPECS.map((s) => [s.id, s]));

export function isRegionalStyleId(id: string): id is RegionalStyleId {
  return SPEC_BY_ID.has(id as RegionalStyleId);
}

export function regionalStyleSpec(id: string): RegionalStyleSpec | null {
  return SPEC_BY_ID.get(id as RegionalStyleId) ?? null;
}

export function regionalStyleDescription(s: RegionalStyleSpec): string {
  return `${s.label} — ${s.topic}를 지역 커뮤니티 익명 제보글로 옮긴 향토 이야기. 훅 → 들은 내력 → 툭 던지는 농담 → 첫 표현을 되받는 순서를 지킨다. 나레이션 전반은 검증된 광역권 생활 사투리를 기본으로 하고, 불확실한 희귀어는 만들지 않는다.`;
}

export function regionalStyleDialectHint(s: RegionalStyleSpec): string {
  return s.dialectRule ?? regionalDefault("검증된 광역권 말끝을 쓰고 근거 없는 도시 고유어를 만들지 마라.");
}

export function regionalStyleVoice(s: RegionalStyleSpec) {
  return REGIONAL_VOICE_PROFILES[s.voiceProfileId];
}

export interface RegionalThemeSpec {
  barColor: string;
  borderColor: string;
  accentColor: string;
}

export const REGIONAL_PROFILE_THEMES: Record<RegionalVoiceProfileId, RegionalThemeSpec> = {
  "capital-standard": { barColor: "#E8EEF6", borderColor: "#A9BBD3", accentColor: "#314E73" },
  "gangwon-inland": { barColor: "#E9F2E8", borderColor: "#A6C5A2", accentColor: "#355F37" },
  "gangwon-east": { barColor: "#E3F1F5", borderColor: "#8DBBC8", accentColor: "#15566A" },
  chungcheong: { barColor: "#F4F0DF", borderColor: "#C7BA78", accentColor: "#6A5B20" },
  jeonbuk: { barColor: "#F5EBDD", borderColor: "#CDAE82", accentColor: "#714A28" },
  "jeonnam-west": { barColor: "#F2E8F4", borderColor: "#C2A0C8", accentColor: "#633D6D" },
  "jeonnam-east": { barColor: "#E5F2EE", borderColor: "#91BFAF", accentColor: "#245E50" },
  "gyeongnam-east": { barColor: "#E4F3F8", borderColor: "#87BFD1", accentColor: "#0E5A73" },
  "gyeongnam-west": { barColor: "#E8F0E5", borderColor: "#A5C19A", accentColor: "#3F6337" },
  "gyeongbuk-east": { barColor: "#F5EEDC", borderColor: "#C9A86A", accentColor: "#75501F" },
  "gyeongbuk-north": { barColor: "#EEE8DF", borderColor: "#BBA58A", accentColor: "#5B4631" },
  jeju: { barColor: "#FFF0DF", borderColor: "#E2AE73", accentColor: "#8A4E1D" },
};

export function regionalThumbFrame(s: RegionalStyleSpec): number {
  return Math.round(s.thumbSceneIndex * 1.4 * 30) + 19;
}
